import { useState, useEffect } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO, applyTheme } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Row, Toggle, Ic } from './shared';
import { CATEGORY_LABELS } from './InsightsShared';
import * as api from '../lib/api';

function ThemeToggle() {
  const [theme, setTheme] = useState('dark');
  useEffect(() => {
    api.getPreferences().then(r => setTheme(r.data?.theme || 'dark')).catch(() => {});
  }, []);
  const toggle = async () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
    try { await api.updatePreferences({ theme: next }); } catch (e) { console.error(e); }
  };
  return (
    <div data-testid="theme-toggle-row" onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--faint)', cursor: 'pointer' }}>
      {theme === 'dark' ? Ic.gear(18, MUTE) : <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={'var(--gold)'} strokeWidth="1.6"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>}
      <div style={{ flex: 1 }}>
        <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>Theme</Bd>
        <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{theme === 'dark' ? 'Dark mode (default)' : 'Light mode'}</Bd>
      </div>
      <Toggle on={theme === 'light'} onToggle={toggle} />
    </div>
  );
}

export function SettingsScreen({ navigate, user, onLogout, onLogin, bankStatus }) {
  const bankDesc = bankStatus?.status === 'connected'
    ? `${bankStatus.connections?.[0]?.institution_name || 'Bank'} · Connected`
    : 'Not connected';
  return (
    <div data-testid="settings-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">

        {/* Guest Mode — Sign In Card */}
        {!user ? (
          <div data-testid="guest-sign-in-card" style={{ marginBottom: 28, padding: 20, background: 'var(--gold-bg)', border: '1px solid var(--gold-border)', borderRadius: 14 }}>
            <Dp style={{ fontSize: 16, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>Sign in to sync your data</Dp>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block', marginBottom: 16, lineHeight: 1.5 }}>Log in to access your debts, track payments, and sync across devices.</Bd>
            <button data-testid="sign-in-btn" onClick={() => navigate('auth-modal')} style={{
              width: '100%', padding: '13px 0', background: GOLD, border: 'none', borderRadius: 10,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, color: '#0A0F1A', cursor: 'pointer', letterSpacing: 1
            }}>SIGN IN / CREATE ACCOUNT</button>
          </div>
        ) : (
          <div className="dettio-row" style={{ gap: 14, marginBottom: 28, padding: '16px 0' }}>
            <div style={{ position: 'relative' }}>
              <div className="dettio-avatar" style={{ width: 52, height: 52, background: `linear-gradient(135deg,${GOLD},${EMBER})`, fontFamily: DISPLAY, fontSize: 20, color: '#111' }}>{(user?.name || 'U')[0]}</div>
              {user?.plan === 'premium' && (
                <div data-testid="premium-avatar-badge" style={{
                  position: 'absolute', bottom: -4, right: -4, width: 20, height: 20,
                  background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', border: '2px solid var(--base)'
                }}>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="#111" stroke="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/>
                  </svg>
                </div>
              )}
            </div>
            <div>
              <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)', display: 'block' }}>{user?.name || 'User'}</Dp>
              {user?.plan === 'premium' ? (
                <div data-testid="premium-plan-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, padding: '4px 10px', background: 'var(--gold-bg)', border: '1px solid var(--gold-border)' }}>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="var(--gold)" stroke="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/>
                  </svg>
                  <Mo style={{ fontSize: 9, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1.5 }}>PREMIUM</Mo>
                </div>
              ) : (
                <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1, marginTop: 4, display: 'block' }}>FREE PLAN</Mo>
              )}
            </div>
          </div>
        )}

        {/* Account rows — only when logged in */}
        {user && (
          <>
            <Label>ACCOUNT</Label>
            <Row icon={Ic.star(18, GOLD)} title="Premium" desc={user?.plan === 'premium' ? 'Active' : 'Upgrade to unlock all features'} onClick={() => navigate('premium')} right={user?.plan === 'premium' ? <Mo data-testid="premium-active-tag" style={{ fontSize: 9, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1, padding: '6px 12px', background: 'var(--gold-bg)', border: '1px solid var(--gold-border)' }}>ACTIVE</Mo> : undefined} />
            <Row icon={Ic.bank(18, MUTE)} title="Bank Connection" desc={bankDesc} onClick={() => navigate('bank-manage')} />
            <Row icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={MUTE} strokeWidth="1.6" strokeLinecap="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>} title="Financial Profile" desc="Edit your income & expenses" onClick={() => navigate('edit-profile')} />
            <Row icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={MUTE} strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>} title="Set Password" desc={user?.has_password ? "Change your login password" : "Add email & password login"} onClick={() => navigate('set-password')} />
          </>
        )}
        <Label>APP</Label>
        <Row icon={Ic.bell(18, MUTE)} title="Notifications" desc="Manage alerts" onClick={() => navigate('notifications')} />
        <Row icon={Ic.chart(18, MUTE)} title="Spending Analysis" desc="Track where money goes" onClick={() => navigate('spending-analysis')} />
        <Row icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>} title="Merchant Rules" desc="Manage category overrides" onClick={() => navigate('merchant-rules')} />
        <Row icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={'var(--gold)'} strokeWidth="1.6" strokeLinecap="round"><path d="M12 2a7 7 0 017 7c0 3-2 5-4 7v3H9v-3c-2-2-4-4-4-7a7 7 0 017-7z" /><line x1="9" y1="19" x2="15" y2="19" /><line x1="10" y1="22" x2="14" y2="22" /></svg>} title="Financial Literacy" desc="Psychology, culture & money tips" onClick={() => navigate('literacy')} />
        <Row icon={Ic.download(18, MUTE)} title="Export Data" desc="Download your records" onClick={() => navigate('export')} />
        <Row icon={Ic.plan(18, GOLD)} title="Custom Strategy" desc="Set your own payoff order" onClick={() => navigate('custom-strategy')} />
        <Label>COMMUNITY</Label>
        <Row icon={Ic.star(18, GOLD)} title="Leaderboard" desc="See how you rank" onClick={() => navigate('leaderboard')} />
        <Row icon={Ic.coach(18, MUTE)} title="Activity Feed" desc="What others are doing" onClick={() => navigate('activity')} />
        <Row icon={Ic.flame(18, GOLD)} title="Points & Streaks" desc={`${user?.streak || 0} month streak`} onClick={() => navigate('points')} />
        <Row icon={Ic.gift(18, MUTE)} title="Refer a Friend" desc="Give 1 month free, earn credit" onClick={() => navigate('referral')} />
        <Label>DISPLAY</Label>
        <ThemeToggle />
        <Label>SUPPORT</Label>
        <Row icon={Ic.coach(18, MUTE)} title="Help & FAQ" desc="Common questions" onClick={() => navigate('help')} />
        
        <Label>LEGAL</Label>
        <Row 
          icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={MUTE} strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M9 9h6M9 15h6"/></svg>}
          title="Privacy Policy" 
          desc="How we protect your data" 
          onClick={() => navigate('privacy')} 
        />
        <Row 
          icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={MUTE} strokeWidth="1.6" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>}
          title="Terms of Service" 
          desc="App usage terms" 
          onClick={() => navigate('terms')} 
        />
        
        <Label>ACCOUNT MANAGEMENT</Label>
        <Row 
          icon={<svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={EMBER} strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
          title="Delete Account" 
          desc="Permanently delete all data" 
          onClick={() => navigate('delete-account')} 
        />
        
        <div style={{ marginTop: 20 }}>
          <button onClick={onLogout} data-testid="logout-btn" style={{
            width: '100%', padding: '14px', background: 'transparent', border: '1px solid var(--ember-border)',
            color: 'var(--ember)', fontFamily: MONO, fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5
          }}>LOG OUT</button>
        </div>
      </div>
    </div>
  );
}

export function NotificationsScreen() {
  const [settings, setSettings] = useState({});
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    api.getNotifications().then(r => setSettings(r.data?.settings || {})).catch(() => setSettings({
      pay: true, confirm: true, roundup: true, coach: true, badge: true, streak: true, weekly: true, budget: false
    }));
    // Check push subscription status
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => { if (sub) setPushEnabled(true); });
      });
    }
  }, []);

  const toggle = async (key) => {
    const upd = { ...settings, [key]: !settings[key] };
    setSettings(upd);
    try { await api.updateNotifications({ settings: upd }); } catch (e) { console.error(e); }
  };

  const togglePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.unsubscribePush(sub.endpoint);
          await sub.unsubscribe();
        }
        setPushEnabled(false);
      } else {
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        });
        await api.subscribePush(sub.toJSON());
        setPushEnabled(true);
      }
    } catch (e) { console.error('Push toggle error:', e); }
    setPushLoading(false);
  };

  const testPush = async () => {
    try { await api.sendTestPush(); } catch (e) { console.error(e); }
  };

  const items = [
    { key: 'pay', label: 'Payment due reminders', desc: 'Before due dates' },
    { key: 'confirm', label: 'Payment confirmations', desc: 'After each payment' },
    { key: 'roundup', label: 'Round-up milestones', desc: 'At $10, $25, $50...' },
    { key: 'coach', label: 'Coach suggestions', desc: 'Weekly optimization tips' },
    { key: 'badge', label: 'Badge unlocks', desc: 'When you earn badges' },
    { key: 'streak', label: 'Streak alerts', desc: 'Streak at risk warnings' },
    { key: 'weekly', label: 'Weekly progress', desc: 'Monday morning summary' },
    { key: 'budget', label: 'Budget alerts', desc: 'Near category limits' },
  ];

  return (
    <div data-testid="notifications-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        {/* Push Notifications */}
        <Label>PUSH NOTIFICATIONS</Label>
        <div data-testid="push-toggle-section" className="dettio-toggle-row">
          {Ic.bell(18, pushEnabled ? GOLD : MUTE)}
          <div className="dettio-flex-1">
            <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>Push Notifications</Bd>
            <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
              {pushEnabled ? 'Receiving push notifications' : 'Enable to get real-time alerts'}
            </Bd>
          </div>
          <Toggle on={pushEnabled} onToggle={togglePush} />
        </div>
        {pushEnabled && (
          <button onClick={testPush} data-testid="test-push-btn" style={{
            padding: '8px 14px', marginTop: 8, marginBottom: 8,
            background: 'var(--gold-bg)', border: '1px solid var(--gold-border)',
            color: 'var(--gold)', fontFamily: MONO, fontSize: 9, cursor: 'pointer', letterSpacing: 0.5,
          }}>SEND TEST NOTIFICATION</button>
        )}

        <Label>ALERTS</Label>
        {items.map(n => (
          <div key={n.key} data-testid={`notif-${n.key}`} className="dettio-toggle-row">
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{n.label}</Bd>
              <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{n.desc}</Bd>
            </div>
            <Toggle on={!!settings[n.key]} onToggle={() => toggle(n.key)} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExportScreen() {
  const [done, setDone] = useState({});
  const items = [
    { key: 'payments', title: 'Payment History', desc: 'All logged payments with dates' },
    { key: 'debts', title: 'Debt Summary', desc: 'Current balances and progress' },
    { key: 'interest', title: 'Interest Report', desc: 'Interest paid vs saved' },
    { key: 'roundups', title: 'Round-Up Log', desc: 'Monthly round-up details' },
    { key: 'all', title: 'Complete Export', desc: 'Everything in one file' },
  ];

  const doExport = async (key) => {
    try { await api.exportData(key); } catch (e) { console.error(e); }
    setDone(p => ({ ...p, [key]: true }));
  };

  return (
    <div data-testid="export-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Label>AVAILABLE EXPORTS</Label>
        {items.map(e => (
          <div key={e.key} data-testid={`export-${e.key}`} className="dettio-row" style={{ gap: 14, padding: '16px 0' }}>
            <div className="dettio-icon-box">{Ic.download(18, MUTE)}</div>
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{e.title}</Bd>
              <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>{e.desc}</Bd>
            </div>
            <button onClick={() => doExport(e.key)} data-testid={`export-btn-${e.key}`} style={{ padding: '8px 14px', background: done[e.key] ? 'var(--gold-bg)' : 'var(--faint)', border: `1px solid ${done[e.key] ? 'var(--gold-border)' : 'var(--ghost)'}`, color: done[e.key] ? 'var(--gold)' : 'var(--mute)', fontFamily: MONO, fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5 }}>{done[e.key] ? 'DONE' : 'DOWNLOAD'}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralScreen() {
  const [data, setData] = useState({ code: 'PEDRO-FREE', referrals: [] });
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.getReferral().then(r => setData(r.data)).catch(() => {}); }, []);

  const copy = () => { navigator.clipboard?.writeText(data.code); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div data-testid="referral-screen" className="dettio-screen">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 12 }}>{Ic.gift(40, GOLD)}</div>
          <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>Give 1 Month Free</Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block' }}>Share your code. They get Premium free for a month. You get credit.</Bd>
        </div>
        <div className="dettio-card" style={{ padding: 20, textAlign: 'center', marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 12 }}>YOUR CODE</Mo>
          <Mo style={{ fontSize: 28, fontWeight: 700, color: 'var(--gold)', letterSpacing: 3, display: 'block', marginBottom: 14 }}>{data.code}</Mo>
          <button onClick={copy} data-testid="copy-code-btn" style={{ padding: '10px 20px', background: 'var(--gold)', border: 'none', color: '#111', fontFamily: MONO, fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5 }}>{copied ? 'COPIED!' : 'COPY CODE'}</button>
        </div>
        {data.referrals?.length > 0 && <>
          <Label>YOUR REFERRALS</Label>
          {data.referrals.map((r, i) => (
            <div key={i} className="dettio-flex dettio-flex--between dettio-flex--center" style={{ padding: '12px 0', borderBottom: '1px solid var(--faint)' }}>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{r.name}</Bd>
              <div style={{ textAlign: 'right' }}>
                <Mo style={{ fontSize: 10, fontWeight: 600, color: r.status === 'Active' ? 'var(--gold)' : 'var(--mute)' }}>{r.status}</Mo>
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{r.joined}</Mo>
              </div>
            </div>
          ))}
        </>}
      </div>
    </div>
  );
}

export function HelpScreen() {
  const [open, setOpen] = useState({});
  const faqs = [
    { q: 'How does snowball work?', a: 'You pay minimums on all debts, then throw every extra dollar at the smallest balance. Once that one is dead, you roll its payment into the next smallest. Momentum builds.' },
    { q: 'Is my bank data safe?', a: 'We use bank-grade encryption and read-only access. We never see your password. Data is encrypted in transit and at rest.' },
    { q: 'What are round-ups?', a: 'Every purchase you make gets rounded up to the nearest dollar. The spare change goes directly to your target debt. $0.47 coffee becomes $1.00 → $0.53 attacks your debt.' },
    { q: 'Can I change my strategy?', a: 'Any time. Go to Strategy tab > tap the strategy card. We\'ll recalculate everything instantly.' },
    { q: 'What if I miss a payment?', a: 'No judgement. Your streak resets but your progress doesn\'t. The app will adjust your plan and help you get back on track.' },
  ];

  return (
    <div data-testid="help-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Label>FREQUENTLY ASKED</Label>
        {faqs.map((f, i) => (
          <div key={i} data-testid={`faq-${i}`} style={{ borderBottom: '1px solid var(--faint)' }}>
            <button onClick={() => setOpen(p => ({ ...p, [i]: !p[i] }))} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{f.q}</Bd>
              <Mo style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>{open[i] ? '−' : '+'}</Mo>
            </button>
            {open[i] && <Bd style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.7, padding: '0 0 16px', display: 'block' }}>{f.a}</Bd>}
          </div>
        ))}
      </div>
    </div>
  );
}


const MERCHANT_API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  { value: 'FOOD_AND_DRINK', label: 'Food & Drink' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'GENERAL_MERCHANDISE', label: 'Shopping' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
  { value: 'RENT_AND_UTILITIES', label: 'Rent & Bills' },
  { value: 'PERSONAL_CARE', label: 'Personal Care' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'GENERAL_SERVICES', label: 'Services' },
  { value: 'HOME_IMPROVEMENT', label: 'Home' },
  { value: 'GOVERNMENT_AND_NON_PROFIT', label: 'Gov & Tax' },
  { value: 'OTHER', label: 'Other' },
  { value: 'VEHICLE_AUTO', label: 'Vehicle / Auto' },
  { value: 'LOAN_DEBT_PAYMENT', label: 'Loan / Debt Payment' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'INVESTMENTS', label: 'Investments' },
  { value: 'BUSINESS_TAXES', label: 'Business Taxes' },
];

const CLASSIFICATION_LABELS = {
  personal: 'Personal', business_cogs: 'Business (COGS)', business_operations: 'Business Ops',
  business_taxes: 'Business Taxes', vehicle_payment: 'Vehicle / Auto', debt_payment: 'Loan / Debt Payment',
  insurance: 'Insurance', investment: 'Investment', ignore: 'Ignored',
};

export function MerchantRulesScreen() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${MERCHANT_API}/api/insights/merchant-rules`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || []);
      }
    } catch (err) { console.error('Failed to load rules:', err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (merchant) => {
    try {
      const res = await fetch(`${MERCHANT_API}/api/insights/merchant-rules/${encodeURIComponent(merchant)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) { setRules(prev => prev.filter(r => r.merchant !== merchant)); setDeleteConfirm(null); }
    } catch (err) { console.error('Failed to delete rule:', err); }
  };

  const handleUpdate = async (merchant) => {
    const specialMap = {
      VEHICLE_AUTO: 'vehicle_payment', LOAN_DEBT_PAYMENT: 'debt_payment',
      INSURANCE: 'insurance', INVESTMENTS: 'investment', BUSINESS_TAXES: 'business_taxes',
    };
    const classification = specialMap[editCategory] || 'personal';
    try {
      const res = await fetch(`${MERCHANT_API}/api/insights/merchant-rules/${encodeURIComponent(merchant)}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classification, category_override: editCategory }),
      });
      if (res.ok) {
        setRules(prev => prev.map(r => r.merchant === merchant 
          ? { ...r, classification, category_override: editCategory, reclassified_at: new Date().toISOString() } : r
        ));
        setEditingRule(null);
      }
    } catch (err) { console.error('Failed to update rule:', err); }
  };

  const getRuleDisplay = (rule) => {
    if (rule.category_override) return CATEGORY_LABELS[rule.category_override] || rule.category_override;
    return CLASSIFICATION_LABELS[rule.classification] || rule.classification;
  };

  return (
    <div data-testid="merchant-rules-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <Mo style={{ fontSize: 9, color: GOLD, letterSpacing: 2, display: 'block', marginBottom: 8 }}>MERCHANT RULES</Mo>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.5, marginBottom: 20 }}>
          Rules override Plaid's default categories. When you re-analyze, all transactions from these merchants will use your assigned category.
        </Bd>
        {loading ? (
          <Mo style={{ fontSize: 12, color: 'var(--mute)', textAlign: 'center', padding: 40 }}>Loading rules...</Mo>
        ) : rules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Mo style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>No merchant rules yet.</Mo>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 8 }}>Reclassify transactions in Spending Analysis to create rules.</Mo>
          </div>
        ) : (
          <div>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginBottom: 12 }}>{rules.length} rule{rules.length !== 1 ? 's' : ''}</Mo>
            {rules.map((rule, i) => (
              <div key={rule.merchant} data-testid={`merchant-rule-${i}`} style={{ padding: '14px 0', borderBottom: '1px solid var(--faint)' }}>
                {editingRule === rule.merchant ? (
                  <div>
                    <Bd style={{ fontSize: 13, color: 'var(--light)', fontWeight: 600, display: 'block', marginBottom: 10 }}>{rule.merchant}</Bd>
                    <select data-testid={`rule-category-select-${i}`} value={editCategory} onChange={e => setEditCategory(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', background: 'var(--faint)', border: '1px solid var(--ghost)', borderRadius: 8, color: 'var(--light)', fontSize: 12, fontFamily: MONO, marginBottom: 10, cursor: 'pointer' }}>
                      {CATEGORY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button data-testid={`rule-save-btn-${i}`} onClick={() => handleUpdate(rule.merchant)}
                        style={{ flex: 1, padding: '10px', background: GOLD, border: 'none', borderRadius: 8, fontFamily: MONO, fontSize: 10, fontWeight: 700, color: '#111', cursor: 'pointer', letterSpacing: 0.5 }}>SAVE</button>
                      <button onClick={() => setEditingRule(null)}
                        style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid var(--ghost)', borderRadius: 8, fontFamily: MONO, fontSize: 10, color: 'var(--mute)', cursor: 'pointer', letterSpacing: 0.5 }}>CANCEL</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Bd style={{ fontSize: 13, color: 'var(--light)', fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rule.merchant}</Bd>
                      <Mo style={{ fontSize: 10, color: GOLD, display: 'block', marginTop: 2 }}>{getRuleDisplay(rule)}</Mo>
                      {rule.reclassified_at && <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginTop: 2 }}>Set {new Date(rule.reclassified_at).toLocaleDateString()}</Mo>}
                    </div>
                    <button data-testid={`rule-edit-btn-${i}`} onClick={() => { setEditingRule(rule.merchant); setEditCategory(rule.category_override || 'OTHER'); }}
                      style={{ padding: '6px 12px', background: 'var(--faint)', border: '1px solid var(--ghost)', borderRadius: 6, fontFamily: MONO, fontSize: 9, color: 'var(--light)', cursor: 'pointer', letterSpacing: 0.5 }}>EDIT</button>
                    <button data-testid={`rule-delete-btn-${i}`} onClick={() => setDeleteConfirm(rule.merchant)}
                      style={{ padding: '6px 12px', background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontFamily: MONO, fontSize: 9, color: EMBER, cursor: 'pointer', letterSpacing: 0.5 }}>DELETE</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {deleteConfirm && (
        <div data-testid="delete-rule-confirm" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setDeleteConfirm(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#1a1a1a', borderRadius: 16, padding: 24, border: '1px solid rgba(255,255,255,0.12)', maxWidth: 340, width: '100%' }}>
            <Bd style={{ fontSize: 14, color: 'var(--light)', display: 'block', marginBottom: 8 }}>Delete rule for "{deleteConfirm}"?</Bd>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 20 }}>This merchant will use Plaid's default category on next analysis.</Mo>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid var(--ghost)', borderRadius: 10, fontFamily: MONO, fontSize: 11, color: 'var(--mute)', cursor: 'pointer' }}>CANCEL</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ flex: 1, padding: '12px', background: EMBER, border: 'none', borderRadius: 10, fontFamily: MONO, fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>DELETE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export function SetPasswordScreen({ navigate, user }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError(''); setStatus('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.setPassword(password);
      setStatus('Password set! You can now log in with your email and password.');
      setPassword(''); setConfirm('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to set password. Try again.');
    } finally { setLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 10, fontFamily: BODY, fontSize: 14, color: 'var(--light)', boxSizing: 'border-box', outline: 'none' };

  return (
    <div data-testid="set-password-screen" className="dettio-screen">
      <Horizon intensity={0.4} />
      <div className="dettio-screen__inner">
        <button onClick={() => navigate('settings')} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontFamily: MONO, fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 24, letterSpacing: 0.5 }}>← BACK</button>
        <Dp style={{ fontSize: 22, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>
          {user?.has_password ? 'Change Password' : 'Add Password Login'}
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 28, lineHeight: 1.5 }}>
          {user?.has_password
            ? 'Update your password below.'
            : `Add a password to your account (${user?.email}). You'll be able to log in with email & password as a backup to Google.`}
        </Bd>

        {status && (
          <div data-testid="set-password-success" style={{ padding: 14, background: 'var(--teal-bg)', border: '1px solid rgba(20,184,166,0.28)', borderRadius: 10, marginBottom: 20 }}>
            <Bd style={{ fontSize: 13, color: '#14B8A6', display: 'block' }}>{status}</Bd>
          </div>
        )}

        {error && (
          <div data-testid="set-password-error" style={{ padding: 14, background: 'var(--ember-bg)', border: '1px solid var(--ember-border)', borderRadius: 10, marginBottom: 20 }}>
            <Bd style={{ fontSize: 13, color: 'var(--ember)', display: 'block' }}>{error}</Bd>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Mo style={{ fontSize: 10, color: 'var(--ghost)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>NEW PASSWORD</Mo>
          <input data-testid="new-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <Mo style={{ fontSize: 10, color: 'var(--ghost)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>CONFIRM PASSWORD</Mo>
          <input data-testid="confirm-password-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" style={inputStyle} />
        </div>

        <button data-testid="set-password-submit-btn" onClick={handleSubmit} disabled={loading} style={{
          width: '100%', padding: 16, background: loading ? 'var(--ghost)' : 'var(--gold)',
          border: 'none', borderRadius: 10, fontFamily: MONO, fontSize: 12, fontWeight: 700,
          color: '#0A0F1A', cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: 1
        }}>
          {loading ? 'SAVING...' : (user?.has_password ? 'UPDATE PASSWORD' : 'SET PASSWORD')}
        </button>

        <div style={{ marginTop: 24, padding: 16, background: 'var(--faint)', borderRadius: 10 }}>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--light)' }}>Forgot your password?</strong><br />
            Log out and use "Forgot Password" on the login screen — we'll email you a reset code.
          </Bd>
        </div>
      </div>
    </div>
  );
}
